import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import Decimal from 'decimal.js';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { BillingType, BillMode, InitialDiscount, ServiceFormDataType } from '~/components/BillModal/BillClientModal.types';
import { discountToDTO } from '~/components/BillModal/utils/BillClientModal.utils';
import { serviceBillingModel, serviceCostModel, serviceTemplateModel } from '~/models';
import { QboIntegrationLineItemType } from '~/models/integration/model';
import { BillingStageType } from '~/models/manual-service-billing';
import { toDatestampString } from '~/models/time';
import { BillingScheduleType } from '~/types/api/billing/submit-charges';
import { utils } from '~/utils';
function seededServiceNote(service, param) {
    let { descriptionEnabled } = param;
    return descriptionEnabled ? service.description : '';
}
export function createEmptyServiceItem(service) {
    let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    return {
        itemId: generateUniqueId('service'),
        type: BillingType.Service,
        note: seededServiceNote(service, options),
        service: {
            type: ServiceFormDataType.Default,
            id: service.id,
            price: '',
            quantity: '',
            cost: service.cost
        },
        fullService: service
    };
}
export const billingStagesTextsPath = 'billing.stages';
export const getStageNote = (param)=>{
    let { stageNumber, stagesLength, t } = param;
    return t.stageNote({
        stageNumber,
        totalStages: stagesLength
    });
};
export const generateStageDescription = (param)=>{
    let { service, stageNumber, t } = param;
    const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
    if (!oneTimeInStages || oneTimeInStages.numStages() === 0) return '';
    return getStageNote({
        stageNumber,
        stagesLength: oneTimeInStages.numStages(),
        t
    });
};
export const MAX_BILL_AMOUNT = new Decimal(999999);
const REQUIRED_ERROR = 'required';
const MAX_ERROR = 'max';
export const validateAmountGreaterThan0 = (value)=>{
    if (!value) return REQUIRED_ERROR;
    const asNumber = new Decimal(+value);
    if (asNumber.lessThanOrEqualTo(0)) return REQUIRED_ERROR;
    if (asNumber.greaterThan(MAX_BILL_AMOUNT)) return MAX_ERROR;
    return undefined;
};
export const validateRangeRate = (value)=>{
    if (!value) return REQUIRED_ERROR;
    if (new Decimal(+value).greaterThan(MAX_BILL_AMOUNT)) return MAX_ERROR;
    return undefined;
};
const toCellState = (filled)=>filled ? 'plain' : 'empty';
const getServiceCellStates = (item)=>{
    if (item.service.type === ServiceFormDataType.Stages) {
        return {
            description: 'input',
            quantity: 'empty',
            rate: 'plain',
            discount: 'empty',
            preapproved: 'empty'
        };
    }
    const { cost } = item.service;
    if (serviceCostModel.isVariablePrice(cost)) {
        return {
            description: 'input',
            quantity: 'input',
            rate: 'plain',
            discount: toCellState(cost.discount != null),
            preapproved: toCellState(cost.unitCap != null)
        };
    }
    if (serviceCostModel.isRangePrice(cost)) {
        return {
            description: 'input',
            quantity: 'empty',
            rate: 'input',
            discount: 'empty',
            preapproved: 'plain'
        };
    }
    return {
        description: 'input',
        quantity: 'empty',
        rate: 'plain',
        discount: 'empty',
        preapproved: 'empty'
    };
};
const getChargeCellStates = (outOfScopeCost)=>({
        description: 'input',
        quantity: 'input',
        rate: outOfScopeCost != null && serviceCostModel.isVariablePrice(outOfScopeCost) ? 'plain' : 'input',
        discount: 'input',
        preapproved: 'empty'
    });
export const getCellStates = (item, outOfScopeCost)=>item.type === BillingType.Service ? getServiceCellStates(item) : getChargeCellStates(outOfScopeCost);
export function createServiceItemWithStages(param) {
    let { service, stageNumber, billingStages, note } = param;
    return {
        itemId: generateUniqueId('service'),
        type: BillingType.Service,
        note: note || '',
        service: {
            type: ServiceFormDataType.Stages,
            id: service.id,
            price: '',
            quantity: '',
            cost: service.cost,
            stageNumber,
            billingStages
        },
        fullService: service
    };
}
export function createDefaultServiceItem(param) {
    let { service, controlData, t, descriptionEnabled } = param;
    const billingStages = controlData.getManualServiceBillingStages(service.id);
    if (serviceBillingModel.isOneTimeInStages(service.billing) && (billingStages === null || billingStages === void 0 ? void 0 : billingStages.length)) {
        const nextAvailableStage = billingStages.filter((stage)=>stage.type === BillingStageType.Available).sort((a, b)=>a.stageNumber - b.stageNumber)[0];
        if (!nextAvailableStage) return null;
        return createServiceItemWithStages({
            service,
            stageNumber: nextAvailableStage.stageNumber,
            billingStages,
            note: getStageNote({
                stagesLength: billingStages.length,
                stageNumber: nextAvailableStage.stageNumber,
                t
            })
        });
    }
    return createEmptyServiceItem(service, {
        descriptionEnabled
    });
}
export function appendItemForSelection(param) {
    let { selection, services, controlData, t, descriptionEnabled } = param;
    var _createDefaultServiceItem;
    if (selection.kind === 'outOfScopeTemplate') {
        return seedChargeItemFromTemplate(selection.template, {
            descriptionEnabled
        });
    }
    const { option } = selection;
    const service = services.find((candidate)=>candidate.id === option.id);
    if (!service) {
        throw new Error("Service with id ".concat(option.id, " not found for bill client modal add charge selection"));
    }
    if (option.billingStages != null && option.stageNumber != null) {
        return createServiceItemWithStages({
            service,
            stageNumber: option.stageNumber,
            billingStages: option.billingStages,
            note: getStageNote({
                stagesLength: option.billingStages.length,
                stageNumber: option.stageNumber,
                t
            })
        });
    }
    return (_createDefaultServiceItem = createDefaultServiceItem({
        service,
        controlData,
        t,
        descriptionEnabled
    })) !== null && _createDefaultServiceItem !== void 0 ? _createDefaultServiceItem : createEmptyServiceItem(service, {
        descriptionEnabled
    });
}
export function overrideItemForSelection(param) {
    let { existingItemId, selection, services, controlData, t, descriptionEnabled } = param;
    const item = appendItemForSelection({
        selection,
        services,
        controlData,
        t,
        descriptionEnabled
    });
    return _object_spread_props(_object_spread({}, item), {
        itemId: existingItemId
    });
}
function templateDiscountValue(template) {
    const discount = serviceTemplateModel.discount(template);
    if (!discount) return InitialDiscount;
    return {
        type: discount.type(),
        amount: discount.amount().toString()
    };
}
export function seedChargeItemFromTemplate(template) {
    let { descriptionEnabled } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    const chargeItem = createEmptyChargeItem();
    const note = descriptionEnabled && template.description ? template.description : '';
    return _object_spread_props(_object_spread({}, chargeItem), {
        name: template.name,
        note,
        charge: _object_spread_props(_object_spread({}, chargeItem.charge), {
            serviceTemplateId: template.id,
            rate: serviceTemplateModel.price(template).abs().toString(),
            quantity: serviceTemplateModel.isFixedPrice(template.cost) ? '1' : '',
            discount: templateDiscountValue(template)
        })
    });
}
export function convertTimeToQuantityInHours(param) {
    let { hours, minutes } = param;
    return +(hours + minutes / 60).toFixed(2);
}
export function createTimeActivityServiceItem(service, timeActivity) {
    return {
        itemId: generateUniqueId('service'),
        timeActivityId: timeActivity.id,
        type: BillingType.Service,
        note: timeActivity.note,
        service: {
            type: ServiceFormDataType.Default,
            id: service.id,
            price: serviceCostModel.isVariablePrice(service.cost) ? utils.formatDollarAmount(serviceCostModel.price(service.cost)) : '',
            quantity: convertTimeToQuantityInHours({
                hours: timeActivity.hours,
                minutes: timeActivity.minutes
            }).toString(),
            cost: service.cost
        },
        fullService: service
    };
}
export function isItemUnselected(item) {
    return item.type === BillingType.Charge && item.name.trim() === '';
}
export function createEmptyChargeItem() {
    return {
        itemId: generateUniqueId('charge'),
        type: BillingType.Charge,
        name: '',
        note: '',
        charge: {
            serviceTemplateId: '',
            quantity: '',
            rate: '',
            discount: InitialDiscount
        }
    };
}
export const mapItemToCharge = (item)=>{
    const { serviceTemplateId, rate, quantity, discount } = item.charge;
    const note = item.note;
    if (!serviceTemplateId) {
        throw new Error('Service is required');
    }
    return {
        serviceTemplateId,
        note: note || undefined,
        rate: rate,
        quantity: quantity,
        discount: discountToDTO(discount)
    };
};
export const mapItemToService = (item, timeActivity)=>{
    const { id, quantity, cost, price } = item.service;
    const { note } = item;
    return {
        serviceId: id,
        quantity: serviceCostModel.isVariablePrice(cost) ? quantity : undefined,
        price: serviceCostModel.isRangePrice(cost) ? price : undefined,
        note: note || undefined,
        stageNumber: item.service.type === ServiceFormDataType.Stages ? item.service.stageNumber : undefined,
        integrations: timeActivity ? [
            {
                type: QboIntegrationLineItemType.QBO,
                value: {
                    timeActivity
                }
            }
        ] : undefined
    };
};
export const getSubmitChargesData = (data, timeActivities, attachments)=>{
    const { issueDate, items, mode } = data;
    const outOfScopeCharges = items.filter((item)=>item.type === BillingType.Charge).map((item)=>mapItemToCharge(item));
    const servicesListedInAgreement = items.filter((item)=>item.type === BillingType.Service).map((item)=>{
        const timeActivity = item.timeActivityId ? timeActivities === null || timeActivities === void 0 ? void 0 : timeActivities.getTimeActivitiesForService(item.service.id).find((ta)=>ta.id === item.timeActivityId) : undefined;
        return mapItemToService(item, timeActivity);
    });
    const shortDate = toDatestampString(issueDate);
    return {
        outOfScopeCharges,
        servicesListedInAgreement,
        billingDetails: {
            issueDate: shortDate,
            type: mode === BillMode.Now ? BillingScheduleType.BillNow : BillingScheduleType.ScheduleBill
        },
        attachments
    };
};
